import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useState } from 'react'
import { Chat } from 'stream-chat-react'

import { env } from 'src/config/env'
import { ChatProvider } from 'src/features/chats/context/chat-context'
import { client } from 'src/lib/constants'
import { queryConfig } from 'src/lib/react-query'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { GenericSuspense } from 'src/shared/components/ui/miscellaneous/generic-suspense'
import { Toaster } from 'src/shared/components/ui/popups/toast/toaster'
import { BottomSheetContextProvider } from 'src/shared/context/bottom-sheet-context'

type AppProviderProps = {
  children: React.ReactNode
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  )

  return (
    <ErrorBoundaryWrapper>
      <ConradHead />
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <BottomSheetContextProvider>
          <Chat client={client}>
            <ChatProvider>
              <GenericSuspense style="fullscreen">{children}</GenericSuspense>
            </ChatProvider>
          </Chat>
        </BottomSheetContextProvider>
        {env.MODE === 'local' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundaryWrapper>
  )
}

const ConradHead = () => {
  return (
    <>
      <title>Conrad | Where homeownership happens</title>
      <meta
        name="description"
        content="For life's biggest moves, we connect you with the right people, and AI takes care of the rest."
      />

      {env.MODE !== 'prod' && <meta name="robots" content="noindex" />}

      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <meta property="og:image" content="https://theconradapp.com/consumer-og-img.jpg" />
      <meta property="og:title" content="Conrad | Where homeownership happens" />
      <meta property="og:site_name" content="Conrad" />
      <meta
        property="og:description"
        content="For life's biggest moves, we connect you with the right people, and AI takes care of the rest."
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image:alt" content="Conrad App" />
      <meta name="twitter:title" content="Conrad | Where homeownership happens" />
      <meta
        name="twitter:description"
        content="For life's biggest moves, we connect you with the right people, and AI takes care of the rest."
      />
      <meta name="twitter:image:src" content="https://theconradapp.com/consumer-og-img.jpg" />
      <meta
        name="keywords"
        content="conrad,referrals,networking,professionals,connect,app,real estate,realtors,real estate agents,real estate professionals,real estate networking,real estate referrals,real estate connections,real estate app,real estate technology,real estate industry,real estate market,real estate business,real estate leads,real estate partnerships,real estate collaboration,real estate community,real estate platform,real estate software,real estate tools,real estate solutions,real estate matchmaking,real estate growth,real estate development,real estate success,real estate opportunities"
      />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#f8f7f5" />
    </>
  )
}
